import * as React from "react";
import { Flex, Text } from "@adobe/react-spectrum";
import { IFilterControl, IFilterItem } from "../../FilterSetupPlaceholder";
import { OperatorSelector, IOperator } from "../OperatorSelector";
import { AtollonTextField } from "../../../../../../ui/TextField/AtollonTextField";
import { useFilterGuard } from "../../../../../../../hooks/useFilterGuard";
import styles from "./NumericFilterControl.module.css";

export interface INumericFilterControlProps {
    filter: IFilterControl;
    onFilterChange: (filter: IFilterControl) => void;
}

const NUMERIC_OPERATORS: IOperator[] = [
    { key: "equals", label: "Equals" },
    { key: "greater", label: "Greater Than" },
    { key: "less", label: "Less Than" },
    { key: "lessOrEquals", label: "Less Than or Equals" },
    { key: "greaterOrEquals", label: "Greater Than or Equals" },
    { key: "between", label: "Between" },
    { key: "not_equals", label: "Not Equals" },
    { key: "is_empty", label: "Is Empty" },
    { key: "is_not_empty", label: "Is Not Empty" },
];

export const NumericFilterControl = React.forwardRef<
    HTMLDivElement,
    INumericFilterControlProps
>((props, forwardedRef) => {
    const { filter, onFilterChange } = props;
    const { notifyChange, syncGuard } = useFilterGuard(onFilterChange);
    // Track if we're updating operator internally to prevent useEffect from resetting it
    const isInternalOperatorUpdateRef = React.useRef<boolean>(false);
    const lastSyncedFilterInfoRef = React.useRef<string>(
        JSON.stringify(filter.filterInfo)
    );
    const currentFilterItem = filter.filterInfo[0] || {
        name: "value",
        type: "equals",
        value: "",
    };

    const [operator, setOperator] = React.useState<string>(() => {
        const initialType = currentFilterItem.type || "equals";
        // Migrate legacy operator to numeric default
        if (initialType === "contains") {
            return "equals";
        }
        return initialType;
    });
    const [value, setValue] = React.useState<number | undefined>(
        currentFilterItem.value
            ? parseFloat(currentFilterItem.value)
            : undefined
    );
    const [fromValue, setFromValue] = React.useState<number | undefined>(
        undefined
    );
    const [toValue, setToValue] = React.useState<number | undefined>(undefined);

    React.useEffect(() => {
        syncGuard(filter);
    }, [filter, syncGuard]);

    React.useEffect(() => {
        // Skip if this is an internal update we just triggered
        if (isInternalOperatorUpdateRef.current) {
            isInternalOperatorUpdateRef.current = false;
            return;
        }

        const currentItem = filter.filterInfo[0];
        if (currentItem) {
            const currentFilterInfoStr = JSON.stringify(filter.filterInfo);
            
            // Only update if filter.filterInfo actually changed from parent
            if (currentFilterInfoStr !== lastSyncedFilterInfoRef.current) {
                let nextOperator = currentItem.type || "equals";
                const isRemovedOperator = nextOperator === "contains";
                if (isRemovedOperator) {
                    nextOperator = "equals";
                }

                setOperator(nextOperator);
                lastSyncedFilterInfoRef.current = currentFilterInfoStr;
                
                if (nextOperator === "between") {
                    // Parse range values if stored as JSON string or separate fields
                    const valueStr = currentItem.value || "";
                    try {
                        const parsed = JSON.parse(valueStr);
                        setFromValue(parsed.from);
                        setToValue(parsed.to);
                    } catch {
                        setFromValue(undefined);
                        setToValue(undefined);
                    }
                } else if (
                    nextOperator === "is_empty" ||
                    nextOperator === "is_not_empty"
                ) {
                    // For is_empty and is_not_empty, value should be empty string
                    setValue(undefined);
                } else {
                    setValue(
                        currentItem.value
                            ? parseFloat(currentItem.value)
                            : undefined
                    );
                }

                // Persist operator migration (contains -> equals) to parent
                if (isRemovedOperator) {
                    const updatedFilterInfo: IFilterItem[] = [
                        {
                            ...currentItem,
                            type: "equals",
                        },
                    ];
                    const didNotify = notifyChange({
                        ...filter,
                        filterInfo: updatedFilterInfo,
                    });
                    if (didNotify) {
                        isInternalOperatorUpdateRef.current = true;
                    }
                }
            } else {
                // Filter info didn't change, but we should still sync value if needed
                // (this handles the case where only value changed, not operator)
                const currentType = currentItem.type || "equals";
                if (currentType === "between") {
                    const valueStr = currentItem.value || "";
                    try {
                        const parsed = JSON.parse(valueStr);
                        setFromValue(parsed.from);
                        setToValue(parsed.to);
                    } catch {
                        setFromValue(undefined);
                        setToValue(undefined);
                    }
                } else if (
                    currentType === "is_empty" ||
                    currentType === "is_not_empty"
                ) {
                    setValue(undefined);
                } else {
                    setValue(
                        currentItem.value
                            ? parseFloat(currentItem.value)
                            : undefined
                    );
                }
            }
        }
    }, [filter.filterInfo, filter, notifyChange]);

    const updateFilterInfo = React.useCallback(
        (op: string, val?: number | string, from?: number, to?: number) => {
            let filterValue: string;
            if (op === "between") {
                filterValue = JSON.stringify({
                    from: from ?? "",
                    to: to ?? "",
                });
            } else {
                filterValue = val?.toString() || "";
            }

            const updatedFilterInfo: IFilterItem[] = [
                {
                    ...currentFilterItem,
                    type: op,
                    value: filterValue,
                },
            ];
            return notifyChange({
                ...filter,
                filterInfo: updatedFilterInfo,
            });
        },
        [filter, currentFilterItem, notifyChange]
    );

    const handleOperatorChange = React.useCallback(
        (newOperator: string) => {
            setOperator(newOperator);
            // For is_empty and is_not_empty operators, set value to empty string
            if (newOperator === "is_empty" || newOperator === "is_not_empty") {
                const didNotify = updateFilterInfo(newOperator, "");
                if (didNotify) {
                    // Mark this as an internal update to prevent useEffect from resetting
                    isInternalOperatorUpdateRef.current = true;
                }
            } else if (newOperator === "between") {
                const didNotify = updateFilterInfo(newOperator, undefined, fromValue, toValue);
                if (didNotify) {
                    isInternalOperatorUpdateRef.current = true;
                }
            } else {
                const didNotify = updateFilterInfo(newOperator, value);
                if (didNotify) {
                    isInternalOperatorUpdateRef.current = true;
                }
            }
        },
        [value, fromValue, toValue, updateFilterInfo]
    );

    const handleValueChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
                ? parseFloat(e.target.value)
                : undefined;
            setValue(newValue);
            updateFilterInfo(operator, newValue);
        },
        [operator, updateFilterInfo]
    );

    const handleFromValueChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
                ? parseFloat(e.target.value)
                : undefined;
            setFromValue(newValue);
            updateFilterInfo(operator, undefined, newValue, toValue);
        },
        [operator, toValue, updateFilterInfo]
    );

    const handleToValueChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
                ? parseFloat(e.target.value)
                : undefined;
            setToValue(newValue);
            updateFilterInfo(operator, undefined, fromValue, newValue);
        },
        [operator, fromValue, updateFilterInfo]
    );

    return (
        <div ref={forwardedRef} className={styles.numericFilterControl}>
            <Flex direction="column" gap="size-200">
                {/* Operator row: Label + Selector */}
                <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="size-200"
                    UNSAFE_className={styles.operatorRow}
                >
                    <Text UNSAFE_className={styles.operatorLabel}>
                        {filter.label || filter.name}
                    </Text>
                    <div className={styles.operatorSelectorWrapper}>
                        <OperatorSelector
                            operators={NUMERIC_OPERATORS}
                            selectedOperator={operator}
                            onOperatorChange={handleOperatorChange}
                            aria-label="Numeric filter operator"
                        />
                    </div>
                </Flex>

                {/* Value input row(s) */}
                {operator !== "is_empty" &&
                    operator !== "is_not_empty" &&
                    (operator === "between" ? (
                        <div className={styles.rangeInputs}>
                            <AtollonTextField
                                value={fromValue?.toString() || ""}
                                onChange={handleFromValueChange}
                                aria-label={`${
                                    filter.label || filter.name
                                } filter from value`}
                                width="100%"
                            />
                            <AtollonTextField
                                value={toValue?.toString() || ""}
                                onChange={handleToValueChange}
                                aria-label={`${
                                    filter.label || filter.name
                                } filter to value`}
                                width="100%"
                            />
                        </div>
                    ) : (
                        <div className={styles.valueInput}>
                            <AtollonTextField
                                value={value?.toString() || ""}
                                onChange={handleValueChange}
                                aria-label={`${
                                    filter.label || filter.name
                                } filter value`}
                                width="100%"
                            />
                        </div>
                    ))}
            </Flex>
        </div>
    );
});

NumericFilterControl.displayName = "NumericFilterControl";
